import { Component, inject } from '@angular/core';
import {
  BehaviorSubject,
  combineLatestWith,
  first,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { UserRepository } from '@data/user/user.store';
import { UserEventId } from '@data/user/user.model';
import { EventTrackingService } from '@core/services/event-tracking/event-tracking.service';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { MatBadge } from '@angular/material/badge';
import { MatButton, MatIconButton } from '@angular/material/button';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { ExtendedModule } from '@ngbracket/ngx-layout/extended';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { APP_CONFIG } from '@core/config/app-config.token';
import { isAnymatch } from '@core/config/brand';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SaveStateRepository } from '@data/save-state/save-state.store';
import { MatchingSessionRepository } from '@data/matching-session/matching-session.store';
import { DialogService } from '@shared/components/dialog/dialog.service';
import { MatchingSession } from '@data/matching-session/matching-session.model';
import { filter, map } from 'rxjs/operators';
import { formatTimeUtcDe, formatWeekdayDateTimeUtcDe } from '@shared/util/date.util';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '@shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { LocalizedPipe } from '@shared/pipes/localized.pipe';
import {
  FlexDirective,
  FlexOrderDirective,
  LayoutAlignDirective,
  LayoutDirective,
  LayoutGapDirective,
} from '@ngbracket/ngx-layout';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { LanguageSelectComponent } from '@shared/components/language-select/language-select.component';
import { MascotDialogComponent } from '@shared/components/mascot-dialog/mascot-dialog.component';
import { MASCOT_DIALOG_PRIORITY } from '@shared/components/mascot-dialog/mascot-dialog-orchestrator.service';
import { MatButtonLoadingDirective } from '@shared/directives/mat-button-loading.directive';
import { AppRouteNames } from '../../app.routing.names';
import { HomeRouteNames } from '../../modules/home/home.routing.names';
import { DownloadRoadmapDirective } from '@shared/directives/download-roadmap.directive';
import { MatchRepository } from '@data/match/match.store';
import moment from 'moment/moment';
import { ThemeService } from '@core/services/theme/theme.service';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Logger } from '@shared/util/logger.util';
import { FRAME_RESTART } from '@core/services/fair-tabs/fair-tabs.service';

@UntilDestroy()
@Component({
  selector: 'recrewt-toolbar-layout',
  templateUrl: './toolbar-layout.component.html',
  styleUrls: ['./toolbar-layout.component.scss'],
  imports: [
    MatToolbar,
    MatToolbarRow,
    MatButton,
    RouterLink,
    RouterLinkActive,
    MatIcon,
    ExtendedModule,
    RouterOutlet,
    AsyncPipe,
    TranslateModule,
    MatIconButton,
    LocalizedPipe,
    FlexOrderDirective,
    LayoutAlignDirective,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    LanguageSelectComponent,
    FlexDirective,
    LayoutDirective,
    MascotDialogComponent,
    MatButtonLoadingDirective,
    DownloadRoadmapDirective,
    LayoutGapDirective,
    MatSlideToggle,
    MatBadge,
  ],
})
export class ToolbarLayoutComponent {
  protected readonly mascotPriority = MASCOT_DIALOG_PRIORITY;

  private appConfig = inject(APP_CONFIG);

  private readonly links = this.appConfig.modules.matching.links;

  readonly showBrowser = !this.appConfig.modules.browser.disabled;

  /** anymatch: the sub-tabs are its ranked matches and all of the fair's offers. */
  readonly stopMode = isAnymatch(this.appConfig);

  /** An embedded job app shows no toolbar of its own. */
  readonly embedded = !!this.appConfig.app.embeddedIn;

  /** Under the main tabs of a fair with a job app (at the bottom on phones) the toolbar stays on top. */
  readonly bottomNav = !this.appConfig.app.jobsTab && !this.embedded;

  readonly timetableOnly = !!this.appConfig.app.timetableOnly;

  readonly timetableDate = this.appConfig.app.timetableDate;

  readonly timetableWaiting =
    !this.timetableDate ||
    moment()
      .endOf('day')
      .isBefore(moment(this.timetableDate, 'DD.MM.YYYY', true));
  readonly browserFirst = this.appConfig.modules.browser.showAtStart;

  readonly downloadButtonText =
    this.appConfig.modules.matching.altDownloadButtonText ?? 'RATED_MATCHES.download_roadmap';

  /** The matching tab also stands for the questionnaire before it (see `story.showToolbar`). */
  protected readonly isMatching =
    location.href.includes('matching/rate') || location.href.includes('/story');

  /** A fair with several sessions lets the visitor switch to another one from the menu. */
  readonly canSwitchSession =
    !this.embedded && (this.appConfig.fair?.availableSessions?.length ?? 0) > 1;

  loading = false;

  showRestartOverlay = false;

  showButtons = true;

  overlayMessage = '';

  private readonly userRepo = inject(UserRepository);
  private readonly eventTracking = inject(EventTrackingService);
  private readonly saveRepo = inject(SaveStateRepository);
  private readonly matchRepo = inject(MatchRepository);

  /** An embedded job app's tabs, in this bar, carry the counts its sub-tab bar shows elsewhere. */
  readonly unratedCount$ = this.matchRepo.unratedMatchesAmount$;
  readonly ratedCount$ = this.matchRepo.ratedMatchesAmount$;
  private readonly sessionRepo = inject(MatchingSessionRepository);
  private readonly dialog = inject(DialogService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);

  readonly isDark$ = this.themeService.isDark$;

  readonly links$ = new BehaviorSubject(this.links);

  readonly enoughMarked$ = this.saveRepo.markedJobsCount$.pipe(
    combineLatestWith(this.matchRepo.markedMatchesCount$),
    map(([markedJobsCount, markedMatchesCount]) => markedJobsCount > 0 || markedMatchesCount > 0),
  );

  readonly storyDone$ = this.saveRepo.selectedSaveState$.pipe(map((it) => !!it?.storyDone));

  /** True while any /timetable child route is active. */
  readonly isTimetableRoute$: Observable<boolean> = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    map((e) => e.urlAfterRedirects),
    startWith(this.router.url),
    map((url) => url.startsWith('/timetable')),
  );

  /** True while the /timetable/overview child route is active. */
  readonly isOverviewRoute$: Observable<boolean> = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    map((e) => e.urlAfterRedirects),
    startWith(this.router.url),
    map((url) => url.startsWith('/timetable/overview')),
  );

  readonly session$: Observable<MatchingSession | undefined> =
    this.saveRepo.selectedSaveState$.pipe(
      map((it) => it?.sessionRef),
      tap((it) => {
        this.links$.next(this.links.filter((link) => !link.session || !it || link.session === it));
      }),
      switchMap((it) => (it ? this.sessionRepo.loadById(it) : of())),
      first(),
    );

  protected restartStoryClicked(storyDone: boolean) {
    this.overlayMessage = storyDone
      ? 'RATED_MATCHES.restart_message'
      : 'RATED_MATCHES.restart_message_story';
    this.showButtons = true;
    this.showRestartOverlay = true;
  }

  protected restartStory() {
    this.eventTracking.track(UserEventId.STORY_RESTARTED);
    this.eventTracking.flush();
    this.overlayMessage = 'RATED_MATCHES.restart_message_confirmed';
    this.showButtons = false;
    this.restartSuccess(Date.now());
  }

  protected abortRestartStory() {
    this.showRestartOverlay = false;
  }

  protected toggleDarkMode() {
    this.themeService.toggle();
  }

  protected markStoryStarted() {
    localStorage.setItem('storyStartedFromBrowser', 'true');
  }

  protected openSessionInfo(session: MatchingSession | undefined) {
    if (!session) {
      return;
    }
    const title = session.name;
    const dates = session.dates.map(
      (it) => `${formatWeekdayDateTimeUtcDe(it.start)} - ${formatTimeUtcDe(it.end)} Uhr`,
    );
    const datesString = dates.join('\n');
    this.dialog.open(ConfirmDialogComponent, {
      title: title,
      message: `
#location_on# **Ort**:
${session.location?.address ?? 'N/A'}

#date_range# **Wann**:
${datesString}
`,
      cancelText: 'OK',
    } as ConfirmDialogData);
  }

  private restartSuccess(startTime: number) {
    let timeout = 2000 - (Date.now() - startTime);
    if (timeout < 0) timeout = 0;
    setTimeout(() => this.resetSession(), timeout);
  }

  /**
   * Short-ID fairs log back in as the same user, so clearing the browser is not a restart:
   * the next code entry would hand the finished save state straight back. The server has
   * to mint a fresh one first. Everywhere else logout drops the user entirely and the next
   * visit creates user and save state together. An embedded job app has its fair's app start
   * over, as that app's own "Neu starten" does (CREW-1554): its start page, then a new pairing
   * key and with it a new job visitor. The fair's app removes this frame, overlay and all.
   */
  private resetSession() {
    const backToStart = () =>
      void this.router.navigate([AppRouteNames.HOME]).then(() => this.userRepo.logoutUser());

    if (this.embedded) {
      window.parent.postMessage(
        { type: FRAME_RESTART },
        new URL(this.appConfig.app.embeddedIn!).origin,
      );
      return;
    }

    if (!this.appConfig.modules.home.loginViaShortId) {
      backToStart();
      return;
    }

    this.userRepo.restartStory().subscribe({
      next: () => backToStart(),
      error: (error) => {
        Logger.error('Error while restarting story:', error);
        backToStart();
      },
    });
  }

  protected navigateToRatedMatches(): void {
    void this.router.navigate(['/matching/rated']);
  }

  protected switchSession(): void {
    void this.router.navigate([AppRouteNames.HOME, HomeRouteNames.SESSION_SELECT]);
  }
}
